import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import invariant from "tiny-invariant";
import { z } from "zod";

import { InAppDocument, InAppDocumentTemplate } from "@oneleet/api_client";

import { useEditorJs } from "./useEditorJs";
import {
  Button,
  FormLabel,
  Input,
  TextArea,
  Tooltip,
} from "@oneleet/components";

const documentModel = z.object({
  title: z.string(),
  description: z.string(),
  content: z
    .object({
      version: z.string().optional(),
      time: z.number().optional(),
      blocks: z.array(z.any()),
    })
    .optional(),
});

export type DocumentModel = z.infer<typeof documentModel>;

export type InAppDocumentSelectionOpts = {
  template?: InAppDocumentTemplate;
  existingDocument?: InAppDocument;
};

export type InAppDocumentEditorProps = {
  template?: InAppDocumentTemplate;
  inAppDocument?: InAppDocument;
  disabled?: boolean;
  disabledMessage?: string;
  submitButtonLabel?: string;
  onSubmit: (newVal: DocumentModel) => Promise<void>;
};

export const InAppDocumentEditor = ({
  template,
  inAppDocument,
  disabled,
  disabledMessage,
  submitButtonLabel = "Save",
  onSubmit,
}: InAppDocumentEditorProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, reset, handleSubmit } = useForm({
    defaultValues: {
      title: inAppDocument?.title || template?.title || "",
      description: inAppDocument?.description || template?.description || "",
    },
    resolver: zodResolver(documentModel),
  });

  const { editorContainerRef, saveContent } = useEditorJs({
    data: inAppDocument?.editorJSBody || template?.editorJSBody,
  });

  return (
    <>
      <form
        onSubmit={handleSubmit(async (formData) => {
          const blocks = await saveContent();

          invariant(blocks, "blocks should be defined");

          setIsSubmitting(true);
          try {
            await onSubmit({
              title: formData.title,
              description: formData.description,
              content: blocks,
            });
          } catch (_) {
            setIsSubmitting(false);
            return;
          }

          setIsSubmitting(false);
          reset();
        })}
      >
        <div className="mt-4 flex flex-col gap-4">
          <div>
            <FormLabel text="Title" />
            <Input {...register("title")} />
          </div>
          <div>
            <FormLabel text="Description" />
            <TextArea {...register("description")} />
          </div>
          <div>
            <FormLabel text="Content" />
            <div
              className="rounded-lg border border-gray-300 shadow-sm"
              ref={editorContainerRef}
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-4">
          <Tooltip
            isDisabled={!disabled || !disabledMessage}
            content={disabledMessage}
          >
            <Button
              type="submit"
              variant="solid"
              label={submitButtonLabel}
              color={"brand"}
              disabled={isSubmitting || disabled}
              isLoading={isSubmitting}
              loadingLabel="Saving..."
            />
          </Tooltip>
        </div>
      </form>
    </>
  );
};

export default InAppDocumentEditor;
